import { FC, ReactNode } from 'react'
import { Box, Paper, Slide, SxProps } from '@mui/material'

interface ButtonActionBarProps {
  /**
   * Controls whether the action bar is visible
   */
  show: boolean
  /**
   * Children components to render inside the action bar (typically buttons or action controls)
   */
  children: ReactNode
  /**
   * Additional styles to apply to the action bar container
   */
  sx?: SxProps
  /**
   * Additional styles to apply to the action bar container wrapper
   */
  containerSx?: SxProps
  /**
   * Elevation level for the Paper component (default: 8)
   */
  elevation?: number
  /**
   * Z-index for the action bar (default: 1200)
   */
  zIndex?: number
  /**
   * Maximum width of the action bar (default: '100%')
   */
  maxWidth?: string | number
  /**
   * Horizontal alignment of content (default: 'center')
   */
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
  /**
   * Gap between child elements (default: 2)
   */
  gap?: number
  /**
   * Padding inside the action bar (default: 2)
   */
  padding?: number | string
}

const ButtonActionBar: FC<ButtonActionBarProps> = ({
  show,
  children,
  sx,
  containerSx,
  elevation = 8,
  zIndex = 1200,
  maxWidth = '100%',
  justifyContent = 'center',
  gap = 2,
  padding = 2,
}) => {
  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex,
          display: 'flex',
          justifyContent: 'center',
          paddingX: 2,
          paddingBottom: 2,
          pointerEvents: 'none',
          ...(containerSx || {}),
        }}
      >
        <Paper
          elevation={elevation}
          sx={{
            width: '100%',
            maxWidth,
            padding,
            borderRadius: '12px',
            pointerEvents: 'auto', // Re-enable pointer events for the paper itself
            ...sx,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent,
              gap,
              flexWrap: 'wrap',
            }}
          >
            {children}
          </Box>
        </Paper>
      </Box>
    </Slide>
  )
}

export default ButtonActionBar
